import Form from "antd/es/form";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import Rate from "common/Rate/Rate";
import styled, { css, useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import React, { CSSProperties, useState } from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Button from "common/Buttons/Button";
import TextArea from "common/Inputs/TextArea";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import { ReviewInput, LanguageNameSpaces } from "@shared/types";
import Flex from "common/Boxes/Flex";
import { openMessage } from "utils/misc";
import { sharedLib } from "@shared/services/shared.service";
import IntercomLink from "common/Text/IntercomLink";
import { MARKETPLACE_REVIEW_GUIDELINES_ARTICLE } from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface NewReviewFormProps {
  subgroupId: number;
  reviewIsEdit: boolean;
  reviewInput: string;
  stars: number;
  onSubmit: () => void;
  disallowedReviewReason?: string;
}

const NewReviewForm = ({
  subgroupId,
  reviewIsEdit,
  reviewInput,
  stars,
  onSubmit,
  disallowedReviewReason,
}: NewReviewFormProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const appTheme = useTheme();
  const [starsEmpty, setStarsEmpty] = useState(false);
  const disabled = !!disallowedReviewReason;
  const disabledStyle: CSSProperties = {
    pointerEvents: disabled ? "none" : "auto",
  };

  const [setGroupReview, { isLoading }] =
    sharedLib.sharedApi.marketplace.useSetGroupReviewMutation();

  const ocClickDisabledSection = () => {
    if (disallowedReviewReason) openMessage(disallowedReviewReason, "warning");
  };

  const onFinish = (values: ReviewInput) => {
    const message = values.reviewInput || reviewInput;
    const stars = values.reviewStars;
    if (stars === 0) {
      setStarsEmpty(true);
      return;
    }
    const reviewRequest = {
      group_id: subgroupId,
      stars: stars,
      message: message,
      is_edit: reviewIsEdit,
    };
    setGroupReview(reviewRequest);
    onSubmit();
  };

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      spinnerSize="large"
      spinnerMarginTop={100}
      overlayOpacity={0.5}
      backgroundColor={appTheme.colors.cardBgColor}
    >
      <Form
        name="new-review"
        onFinish={onFinish}
        component={appTheme.isNewLook ? NewLookFormPanel : undefined}
      >
        <Flex
          $flexDirection="column"
          $alignItems="end"
          style={{ marginBottom: 10 }}
        >
          <div style={{ width: "100%" }}>
            <TitleStarsWrapper>
              <Title type={"secondary"} level={4} style={{ padding: 4 }}>
                {t(
                  reviewIsEdit ? "reviews.edit_review" : "reviews.leave_review"
                )}
                <IntercomLink
                  articleId={MARKETPLACE_REVIEW_GUIDELINES_ARTICLE}
                  style={{
                    whiteSpace: "nowrap",
                    fontSize: "inherit",
                    marginLeft: 8,
                  }}
                >
                  {t("reviews.guidelines")}
                </IntercomLink>
              </Title>
              <DisabledSectionWrapper
                $disabled={disabled}
                onClick={ocClickDisabledSection}
              >
                <Form.Item
                  name="reviewStars"
                  rules={[
                    {
                      type: "number",
                    },
                  ]}
                  style={{
                    marginBottom: 0,
                    ...disabledStyle,
                  }}
                  initialValue={stars}
                >
                  <Rate
                    onChange={() => setStarsEmpty(false)}
                    style={{ fontSize: isMobile ? 20 : 24 }}
                  />
                </Form.Item>
              </DisabledSectionWrapper>
            </TitleStarsWrapper>
            {disallowedReviewReason && (
              <WarningMessage>{disallowedReviewReason}</WarningMessage>
            )}
          </div>
          {!disabled && (
            <RequiredFieldMessage
              requirement={starsEmpty}
              errorMessage={t("reviews.rating_required")}
            />
          )}
        </Flex>
        <DisabledSectionWrapper
          $disabled={disabled}
          onClick={ocClickDisabledSection}
        >
          <Form.Item
            name="reviewInput"
            rules={[{ type: "string" }]}
            style={{
              marginBottom: 0,
              ...disabledStyle,
            }}
          >
            <InputWrapper>
              <StyledTextArea
                placeholder={t("reviews.review_placeholder")}
                defaultValue={reviewInput}
                size="large"
                rows={4}
                autoSize={{ minRows: 4 }}
                showCount
                maxLength={400}
                variant="borderless"
              />
            </InputWrapper>
          </Form.Item>
          <Form.Item>
            <StyledButton
              htmlType="submit"
              style={{
                padding: isMobile ? "4px 22px" : "4px 28px",
                ...disabledStyle,
              }}
            >
              {t("reviews.submit")}
            </StyledButton>
          </Form.Item>
        </DisabledSectionWrapper>
      </Form>
    </LoadingSpinnerOverlay>
  );
};

export default NewReviewForm;

const TitleStarsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;
const InputWrapper = styled.div`
  padding: 12px 12px 0 12px;
  width: 100%;
  height: auto;
  cursor: pointer;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.titleCardBgColor};
  margin-bottom: 20px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding: 10px 12px 0;
      margin-bottom: 12px;
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: ${getNewLookTokens(p.theme).panel};
      box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairlineStrong};
      transition: box-shadow 150ms;
      &:focus-within {
        box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).accent},
          0 0 0 3px ${getNewLookTokens(p.theme).focus};
      }
    `}
`;
/**
 * New look: the review form as a panel; the title in the panel-title role,
 * the counter under the field and Submit on the right.
 */
const NewLookFormPanel = styled.form`
  margin-bottom: 16px;
  padding: 16px 20px 20px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  text-align: left;
  h4 {
    ${newLookType.panelTitle};
    color: ${(p) => getNewLookTokens(p.theme).ink} !important;
    padding: 0 !important;
  }
  h4 a {
    ${newLookType.label};
    color: ${(p) => getNewLookTokens(p.theme).accent};
  }
  .ant-input-data-count {
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  .ant-rate {
    font-size: 20px !important;
  }
  .ant-form-item:last-child {
    margin-bottom: 0;
    .ant-form-item-control-input-content {
      display: flex;
      justify-content: flex-end;
    }
  }
  .ant-input-textarea-show-count .ant-input-data-count {
    right: auto;
    left: 0;
    bottom: -36px;
  }
`;
const StyledButton = styled(Button)`
  border-radius: 8px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-left: 2px;
`;
const DisabledSectionWrapper = styled.div<{ $disabled: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
       opacity: 0.3;
    `};
`;
const WarningMessage = styled(Text)`
  color: ${(p) =>
    p.theme.isNewLook ? getNewLookTokens(p.theme).stop : p.theme.colors.gold};
  padding: 4px;
`;
const StyledTextArea = styled(TextArea)`
  && textarea.ant-input {
    background-color: ${(p) =>
      p.theme.isNewLook
        ? p.theme.colors.cardBgColor
        : p.theme.colors.titleCardBgColor} !important;
  }

  && textarea.ant-input::placeholder {
    color: ${(p) => p.theme.colors.secondaryGrey} !important;
    opacity: 1;
  }

  &.ant-input-textarea-show-count .ant-input-data-count {
    bottom: -28px;
  }
  &.ant-input-affix-wrapper-lg {
    font-size: ${(p) => p.theme.typography.fontSizes.M};
  }
`;
